'use strict';

/* ── yuniq-card-preview.js ───────────────────────────────────────────────────
   Lightweight <yuniq-card-preview> web component.
   Renders a live canvas preview: finish image + motif engraving + name text.
   Core rendering pipeline extracted from yuniq-card-editor.js.

   Usage:
     <yuniq-card-preview data-texture-url="..."></yuniq-card-preview>

   JS API:
     element.update({ blankUrl, blankKey, motifUrl, nameText })
   ─────────────────────────────────────────────────────────────────────────── */

class YuniqCardPreview extends HTMLElement {
  // ── Constants (same as YuniqCardEditor) ───────────────────────────────
  static #CARD_W = 856;
  static #CARD_H = 540;
  static #NAME_FONT_SIZE_BASE  = 28;
  static #NAME_FONT_SIZE_MIN   = 12;
  static #NAME_MAX_WIDTH       = 340;
  static #NAME_LETTER_SPACING  = 1.5;
  static #MOTIF_HEIGHT_RATIO   = 0.40;
  static #MOTIF_CENTER_Y_RATIO = 0.47;
  static #FONT_FAMILY = '"OCR A Std","OCR A Extended","Courier New",monospace';
  static #FRAME_BOUNDS = { x: 0.2005, y: 0.238, w: 0.596, h: 0.517 };

  static #NAME_SAFE_ZONES = {
    none:     { insetX: 16, insetY: 14, maxWidthRatio: 0.46 },
    frame_4:  { insetX: 46, insetY: 38, maxWidthRatio: 0.37 },
    frame_5:  { insetX: 36, insetY: 32, maxWidthRatio: 0.42 },
    frame_6:  { insetX: 44, insetY: 36, maxWidthRatio: 0.37 },
    frame_7:  { insetX: 16, insetY: 14, maxWidthRatio: 0.46 },
    frame_8:  { insetX: 22, insetY: 20, maxWidthRatio: 0.44 },
    frame_9:  { insetX: 42, insetY: 26, maxWidthRatio: 0.40 },
    frame_10: { insetX: 42, insetY: 30, maxWidthRatio: 0.38 },
    frame_11: { insetX: 54, insetY: 32, maxWidthRatio: 0.35 },
    frame_12: { insetX: 24, insetY: 20, maxWidthRatio: 0.43 },
  };

  static #MOTIF_LAYOUT_OVERRIDES = {
    always_thank_god:            { heightRatio: 0.2, centerYRatio: 0.5, centerXRatio: 0.68, maxWidthRatio: 0.56, avoidChip: true },
    spartan:                     { avoidChip: false },
    your_signature_worldwide:    { maxWidthRatio: 0.9 },
    discipline:                  { },
    millionaires_club_signature: { },
    marina_yacht_club:           { },
    dark_dreams:                 { },
    the_emperor:                 { },
    yacht_attitude:              { },
  };

  // ── State ──────────────────────────────────────────────────────────────
  #state = { blankUrl: null, blankKey: '', motifUrl: null, motifHandle: '', motifOffsetX: null, motifOffsetY: null, motifScale: null, motifScaleX: null, nameText: '', frameUrl: null, frameKey: 'none', uploadDataUrl: null, uploadRenderMode: 'ghost', uploadOffsetX: null, uploadOffsetY: null, uploadScale: null };

  // ── Internals ──────────────────────────────────────────────────────────
  #canvas              = null;
  #imgCache            = new Map();
  #motifMaskCache      = new Map();
  #processedBlankCache = new Map();
  #rafId               = null;
  #renderToken         = 0;
  #bufScale            = 1;
  #resizeObserver      = null;

  // ── Lifecycle ──────────────────────────────────────────────────────────

  connectedCallback() {
    this.innerHTML = '<canvas style="width:100%;display:block;touch-action:none;"></canvas>';
    this.#canvas   = this.querySelector('canvas');
    this.#sizeCanvas();
    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#sizeCanvas()) this.#scheduleRender();
    });
    this.#resizeObserver.observe(this);
    this.#scheduleRender();
  }

  disconnectedCallback() {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#resizeObserver?.disconnect();
  }

  // ── Public API ─────────────────────────────────────────────────────────

  update({ blankUrl = null, blankKey = '', motifUrl = null, motifHandle = '', motifOffsetX = null, motifOffsetY = null, motifScale = null, motifScaleX = null, nameText = '', frameUrl = null, frameKey = 'none', uploadDataUrl = null, uploadRenderMode = 'ghost', uploadOffsetX = null, uploadOffsetY = null, uploadScale = null } = {}) {
    this.#state.blankUrl        = blankUrl;
    this.#state.blankKey        = blankKey || '';
    this.#state.motifUrl        = motifUrl;
    this.#state.motifHandle     = motifHandle || '';
    this.#state.motifOffsetX    = motifOffsetX != null ? parseFloat(motifOffsetX) : null;
    this.#state.motifOffsetY    = motifOffsetY != null ? parseFloat(motifOffsetY) : null;
    this.#state.motifScale      = motifScale   != null ? parseFloat(motifScale)   : null;
    this.#state.motifScaleX     = motifScaleX  != null ? parseFloat(motifScaleX)  : null;
    this.#state.nameText        = nameText;
    this.#state.frameUrl        = frameUrl ?? null;
    this.#state.frameKey        = frameKey || 'none';
    this.#state.uploadDataUrl   = uploadDataUrl ?? null;
    this.#state.uploadRenderMode = uploadRenderMode || 'ghost';
    this.#state.uploadOffsetX   = uploadOffsetX != null ? parseFloat(uploadOffsetX) : null;
    this.#state.uploadOffsetY   = uploadOffsetY != null ? parseFloat(uploadOffsetY) : null;
    this.#state.uploadScale     = uploadScale   != null ? parseFloat(uploadScale)   : null;
    this.#scheduleRender();
  }

  // ── Canvas sizing ──────────────────────────────────────────────────────

  #sizeCanvas() {
    const canvas = this.#canvas;
    if (!canvas) return false;
    const w = this.clientWidth || this.offsetWidth || 400;
    if (!w) return false;
    const rect       = this.getBoundingClientRect();
    const stageScale = (rect.width > 0) ? rect.width / w : 1;
    const dpr  = Math.min((window.devicePixelRatio || 1) * stageScale, 4);
    const bufW = Math.round(w * dpr);
    const bufH = Math.round(bufW * YuniqCardPreview.#CARD_H / YuniqCardPreview.#CARD_W);
    this.#bufScale = bufW / YuniqCardPreview.#CARD_W;
    if (canvas.width !== bufW || canvas.height !== bufH) {
      canvas.width  = bufW;
      canvas.height = bufH;
      this.#motifMaskCache.clear();
      this.#processedBlankCache.clear();
      return true;
    }
    return false;
  }

  #makeOffscreen(W, H) {
    const c   = document.createElement('canvas');
    const s   = this.#bufScale;
    c.width   = Math.round(W * s);
    c.height  = Math.round(H * s);
    const ctx = c.getContext('2d');
    if (ctx) {
      ctx.setTransform(s, 0, 0, s, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
    }
    return { canvas: c, ctx };
  }

  // ── Render pipeline ────────────────────────────────────────────────────

  #scheduleRender() {
    const token = ++this.#renderToken;
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#render(token).catch(console.error);
    });
  }

  async #render(token) {
    const canvas = this.#canvas;
    if (!canvas) return;
    if (!canvas.getContext('2d')) return;

    const W     = YuniqCardPreview.#CARD_W;
    const H     = YuniqCardPreview.#CARD_H;
    const scale = canvas.width / W;

    // Load images first — most are cached after the first render
    const [blankImg, motifImg, textureImg, frameImg, uploadImg] = await Promise.all([
      this.#loadImage(this.#state.blankUrl),
      this.#loadImage(this.#state.motifUrl),
      this.#loadImage(this.dataset.textureUrl || null),
      this.#loadImage(this.#state.frameUrl),
      this.#loadImage(this.#state.uploadDataUrl),
    ]);
    if (token !== this.#renderToken) return;

    // Draw to an offscreen canvas so the visible canvas never shows a blank frame
    const off = document.createElement('canvas');
    off.width  = canvas.width;
    off.height = canvas.height;
    const ctx  = off.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, W, H);

    // 1. Background card
    if (blankImg) {
      const src = this.#getStrippedBlank(blankImg) || blankImg;
      ctx.drawImage(src, 0, 0, W, H);
    } else {
      return;
    }

    const bounds = this.#getFrameBounds(W, H);

    // 2. Motif / upload (upload takes priority, clipped to card rounded corners)
    if ((this.#state.uploadDataUrl && uploadImg) || motifImg) {
      const cardRect   = this.#getBackCardRect(blankImg, W, H);
      const cardRadius = Math.round(cardRect.h * 0.06);
      ctx.save();
      this.#roundRect(ctx, cardRect.x, cardRect.y, cardRect.w, cardRect.h, cardRadius);
      ctx.clip();
      if (this.#state.uploadDataUrl && uploadImg) {
        this.#drawUploadedDesign(ctx, uploadImg, textureImg, W, H);
      } else {
        this.#drawEngraving(ctx, motifImg, textureImg, W, H);
      }
      ctx.restore();
    }

    // 3. Personalization name
    if (this.#state.nameText?.trim()) {
      this.#drawName(ctx, this.#state.nameText.trim(), bounds, textureImg, W, H);
    }

    // 4. Frame overlay
    if (frameImg) {
      this.#drawFrameOverlay(ctx, frameImg, textureImg, bounds, W, H);
    }

    // Atomic flip: copy offscreen to visible canvas — no blank-frame flash
    const visCtx = canvas.getContext('2d');
    visCtx.clearRect(0, 0, canvas.width, canvas.height);
    visCtx.drawImage(off, 0, 0);
  }

  // ── Engraving ──────────────────────────────────────────────────────────

  #getMotifLayout() {
    const base = {
      heightRatio:  YuniqCardPreview.#MOTIF_HEIGHT_RATIO,
      centerYRatio: YuniqCardPreview.#MOTIF_CENTER_Y_RATIO,
      centerXRatio: 0.5,
      maxWidthRatio: null,
      avoidChip: true,
    };
    const key = (this.#state.motifHandle || '').replace(/-/g, '_').toLowerCase();
    const ov  = YuniqCardPreview.#MOTIF_LAYOUT_OVERRIDES[key] || {};
    const layout = {
      heightRatio:  ov.heightRatio  ?? base.heightRatio,
      centerYRatio: ov.centerYRatio ?? base.centerYRatio,
      centerXRatio: ov.centerXRatio ?? base.centerXRatio,
      maxWidthRatio: ov.maxWidthRatio ?? base.maxWidthRatio,
      avoidChip:    ov.avoidChip    ?? base.avoidChip,
    };
    if (this.#state.motifOffsetX != null) layout.centerXRatio = Math.max(0.08, Math.min(0.92, this.#state.motifOffsetX));
    if (this.#state.motifOffsetY != null) layout.centerYRatio = Math.max(0.12, Math.min(0.88, this.#state.motifOffsetY));
    if (this.#state.motifScale   != null) layout.heightRatio  = Math.max(0.08, Math.min(0.6,  this.#state.motifScale));
    return layout;
  }

  #drawEngraving(ctx, motifImg, textureImg, W, H) {
    motifImg = this.#toLumaMask(motifImg);

    const layout       = this.#getMotifLayout();
    const motifAspect  = motifImg.width / Math.max(1, motifImg.height);
    const isWideMotif  = motifAspect >= 1.9;
    const heightRatio  = isWideMotif ? Math.min(layout.heightRatio, 0.26) : layout.heightRatio;
    const resolvedMaxWidthRatio = layout.maxWidthRatio || (isWideMotif ? 0.74 : null);
    const maxMotifW    = resolvedMaxWidthRatio ? W * resolvedMaxWidthRatio : Number.POSITIVE_INFINITY;

    const hasManualOverrides = this.#state.motifOffsetX != null || this.#state.motifOffsetY != null || this.#state.motifScale != null;
    const chipRect  = (!hasManualOverrides && layout.avoidChip) ? this.#getChipSafeRect(W, H) : null;
    const placement = this.#resolveMotifPlacement({
      motifImg, W, H,
      targetHeightRatio: heightRatio,
      maxWidth:     maxMotifW,
      centerXRatio: layout.centerXRatio,
      centerYRatio: layout.centerYRatio,
      chipRect,
      scaleX: this.#state.motifScaleX ?? null,
    });
    const { motifW, motifH, motifX, motifY } = placement;

    const { alpha: outlineAlpha, pad: outlinePad } = this.#getMotifOutlineAlpha();
    let olCanvas = null;
    if (outlineAlpha > 0) {
      const pad = outlinePad;
      const { canvas: oc_canvas, ctx: oc } = this.#makeOffscreen(W, H);
      if (oc) {
        oc.drawImage(motifImg, motifX - pad, motifY - pad, motifW + pad * 2, motifH + pad * 2);
        oc.globalCompositeOperation = 'source-in';
        oc.fillStyle = `rgba(0,0,0,${outlineAlpha})`;
        oc.fillRect(0, 0, W, H);
        olCanvas = oc_canvas;
      }
    }

    const isGoldOnBlack = /black_(matt_gold|gold_matt)/.test(this.#state.blankKey);
    const isSilver      = /silver/.test(this.#state.blankKey || '');
    const engStyle      = this.#getMotifEngravingStyle();

    const { canvas: eng, ctx: ec } = this.#makeOffscreen(W, H);
    this.#paintBrushedMetal(ec, textureImg, motifX, motifY, motifW, motifH, {
      seed:     11.3,
      sheen:    isGoldOnBlack ? 0.18 : (isSilver ? 0.36 : 0.42),
      streak:   isGoldOnBlack ? 0.16 : (isSilver ? 0.16 : 0.18),
      contrast: isGoldOnBlack ? 1.16 : (isSilver ? 1.16 : 1.2),
      baseTop: '#e2e2e2', baseMid: '#ffffff', baseBottom: '#c4c4c4',
      tintColor:    engStyle.tintColor,
      tintAlpha:    engStyle.tintAlpha,
      darkenAlpha:  engStyle.darkenAlpha,
      brightenAlpha: engStyle.brightenAlpha,
    });
    ec.globalCompositeOperation = 'destination-in';
    ec.drawImage(motifImg, motifX, motifY, motifW, motifH);
    ec.globalCompositeOperation = 'source-over';

    // Top-left highlight
    const { canvas: hlCanvas, ctx: hc } = this.#makeOffscreen(W, H);
    if (hc) {
      hc.drawImage(motifImg, motifX, motifY, motifW, motifH);
      hc.globalCompositeOperation = 'source-in';
      const hlGrad = hc.createLinearGradient(motifX, motifY, motifX + motifW * 0.3, motifY + motifH * 0.3);
      hlGrad.addColorStop(0, 'rgba(255,255,255,0.55)');
      hlGrad.addColorStop(1, 'rgba(255,255,255,0)');
      hc.fillStyle = hlGrad;
      hc.fillRect(0, 0, W, H);
    }

    // Bottom-right shadow
    const { canvas: shCanvas, ctx: dc } = this.#makeOffscreen(W, H);
    if (dc) {
      dc.drawImage(motifImg, motifX, motifY, motifW, motifH);
      dc.globalCompositeOperation = 'source-in';
      const shGrad = dc.createLinearGradient(motifX + motifW, motifY + motifH, motifX + motifW * 0.7, motifY + motifH * 0.7);
      shGrad.addColorStop(0, 'rgba(0,0,0,0.38)');
      shGrad.addColorStop(1, 'rgba(0,0,0,0)');
      dc.fillStyle = shGrad;
      dc.fillRect(0, 0, W, H);
    }

    // Groove shadow for gold-on-black
    if (isGoldOnBlack) {
      const { canvas: grooveCanvas, ctx: gc } = this.#makeOffscreen(W, H);
      if (gc) {
        gc.drawImage(motifImg, motifX - 1, motifY,     motifW, motifH);
        gc.drawImage(motifImg, motifX + 1, motifY,     motifW, motifH);
        gc.drawImage(motifImg, motifX,     motifY - 1, motifW, motifH);
        gc.drawImage(motifImg, motifX,     motifY + 1, motifW, motifH);
        gc.globalCompositeOperation = 'source-in';
        gc.fillStyle = 'rgba(0,0,0,0.18)';
        gc.fillRect(0, 0, W, H);
      }
      ctx.drawImage(grooveCanvas, 0, 0, W, H);
    }

    if (olCanvas) ctx.drawImage(olCanvas, 0, 0, W, H);
    ctx.shadowColor = isGoldOnBlack ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.22)';
    ctx.shadowBlur  = isGoldOnBlack ? 5 : 8;
    ctx.drawImage(eng, 0, 0, W, H);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur  = 0;
    ctx.drawImage(hlCanvas, 0, 0, W, H);
    ctx.drawImage(shCanvas, 0, 0, W, H);
  }

  // ── Upload design ──────────────────────────────────────────────────────

  #processUploadForEngraving(img) {
    const MAX   = 600;
    const src_w = img.naturalWidth  || img.width;
    const src_h = img.naturalHeight || img.height;
    if (!src_w || !src_h) return img;
    const s   = Math.min(1, MAX / Math.max(src_w, src_h));
    const w   = Math.round(src_w * s);
    const h   = Math.round(src_h * s);
    const c   = document.createElement('canvas');
    c.width   = w;
    c.height  = h;
    const ctx = c.getContext('2d');
    if (!ctx) return img;
    ctx.drawImage(img, 0, 0, w, h);
    try { this.#stripBackground(c, ctx); } catch (_) {}
    return c;
  }

  #drawUploadedDesign(ctx, uploadImg, textureImg, W, H) {
    const renderMode = this.#state.uploadRenderMode || 'ghost';

    // uploadScale = fraction of card width (0.08–0.90), independent of image resolution.
    // Using card-width fractions avoids the minScale clamp in #resolveMotifPlacement that
    // would otherwise lock large images (e.g. photos) to the same size regardless of slider.
    const uploadScale = this.#state.uploadScale ?? YuniqCardPreview.#MOTIF_HEIGHT_RATIO;
    const clamped     = Math.max(0.08, Math.min(0.90, uploadScale));
    const imgW        = (uploadImg.naturalWidth  || uploadImg.width)  || 1;
    const imgH        = (uploadImg.naturalHeight || uploadImg.height) || 1;
    const aspect      = imgW / imgH;
    const motifW      = W * clamped;
    const motifH      = motifW / Math.max(0.01, aspect);

    const cx    = W * (this.#state.uploadOffsetX ?? 0.5);
    const cy    = H * (this.#state.uploadOffsetY ?? YuniqCardPreview.#MOTIF_CENTER_Y_RATIO);
    const motifX = cx - motifW / 2;
    const motifY = cy - motifH / 2;

    if (renderMode === 'ghost') {
      const { canvas: ghCanvas, ctx: gc } = this.#makeOffscreen(W, H);
      if (gc) {
        gc.globalAlpha = 0.42;
        gc.drawImage(uploadImg, motifX, motifY, motifW, motifH);
        gc.globalAlpha = 1;
      }
      ctx.drawImage(ghCanvas, 0, 0, W, H);
      return;
    }

    // engrave — brushed metal clipped to upload alpha (no toLumaMask)
    const processedImg  = this.#processUploadForEngraving(uploadImg);
    const isGoldOnBlack = /black_(matt_gold|gold_matt)/.test(this.#state.blankKey);
    const isSilver      = /silver/.test(this.#state.blankKey || '');
    const engStyle      = this.#getMotifEngravingStyle();

    const { canvas: eng, ctx: ec } = this.#makeOffscreen(W, H);
    this.#paintBrushedMetal(ec, textureImg, motifX, motifY, motifW, motifH, {
      seed:         11.3,
      sheen:        isGoldOnBlack ? 0.18 : (isSilver ? 0.36 : 0.42),
      streak:       isGoldOnBlack ? 0.16 : (isSilver ? 0.16 : 0.18),
      contrast:     isGoldOnBlack ? 1.16 : (isSilver ? 1.16 : 1.2),
      baseTop:      '#e2e2e2', baseMid: '#ffffff', baseBottom: '#c4c4c4',
      tintColor:    engStyle.tintColor,
      tintAlpha:    engStyle.tintAlpha,
      darkenAlpha:  engStyle.darkenAlpha,
      brightenAlpha: engStyle.brightenAlpha,
    });
    ec.globalCompositeOperation = 'destination-in';
    ec.drawImage(processedImg, motifX, motifY, motifW, motifH);
    ec.globalCompositeOperation = 'source-over';

    ctx.drawImage(eng, 0, 0, W, H);
  }

  // ── Name ───────────────────────────────────────────────────────────────

  #drawName(ctx, text, bounds, textureImg, W, H) {
    if (!text) return;
    const spacing   = YuniqCardPreview.#NAME_LETTER_SPACING;
    const safe      = YuniqCardPreview.#NAME_SAFE_ZONES[this.#state.frameKey]
                   || YuniqCardPreview.#NAME_SAFE_ZONES.none;
    const x         = bounds.x + safe.insetX;
    const y         = bounds.y + bounds.h - safe.insetY;
    const maxWidth  = Math.min(
      YuniqCardPreview.#NAME_MAX_WIDTH,
      Math.max(120, bounds.w * safe.maxWidthRatio)
    );
    const fontFamily = YuniqCardPreview.#FONT_FAMILY;

    let fontSize = YuniqCardPreview.#NAME_FONT_SIZE_BASE;
    ctx.font = `${fontSize}px ${fontFamily}`;
    while (this.#measureTextWidth(ctx, text, spacing) > maxWidth
           && fontSize > YuniqCardPreview.#NAME_FONT_SIZE_MIN) {
      fontSize--;
      ctx.font = `${fontSize}px ${fontFamily}`;
    }

    const textW = this.#measureTextWidth(ctx, text, spacing);
    const pad   = Math.ceil(fontSize * 0.15);
    const bx    = x - pad;
    const by    = y - fontSize - pad;
    const bw    = Math.max(1, textW + pad * 2);
    const bh    = Math.max(1, fontSize + pad * 2 + Math.ceil(fontSize * 0.25));

    // Text mask
    const { canvas: maskCanvas, ctx: mc } = this.#makeOffscreen(W, H);
    if (!mc) return;
    mc.font         = `${fontSize}px ${fontFamily}`;
    mc.textBaseline = 'alphabetic';
    mc.fillStyle    = '#fff';
    this.#fillTextWithSpacing(mc, text, x, y, spacing);

    // Brushed-metal layer clipped to text
    const { canvas: texCanvas, ctx: tc } = this.#makeOffscreen(W, H);
    if (!tc) return;
    const engStyle = this.#getMotifEngravingStyle();
    this.#paintBrushedMetal(tc, textureImg, bx, by, bw, bh, {
      seed: 71.3,
      sheen:        /black_(matt_gold|gold_matt)/.test(this.#state.blankKey) ? 0.18 : 0.30,
      streak:       0.10,
      contrast:     1.05,
      baseTop:      '#e0e0e0',
      baseMid:      '#f8f8f8',
      baseBottom:   '#c8c8c8',
      tintColor:    engStyle.tintColor,
      tintAlpha:    engStyle.tintAlpha,
      darkenAlpha:  engStyle.darkenAlpha,
      brightenAlpha: engStyle.brightenAlpha,
    });
    tc.globalCompositeOperation = 'destination-in';
    tc.drawImage(maskCanvas, 0, 0, W, H);
    tc.globalCompositeOperation = 'source-over';

    // Outline behind text
    const { alpha: outlineAlpha, pad: outlinePad } = this.#getMotifOutlineAlpha();
    if (outlineAlpha > 0) {
      const { canvas: olCanvas, ctx: oc } = this.#makeOffscreen(W, H);
      if (oc) {
        oc.font         = `${fontSize}px ${fontFamily}`;
        oc.textBaseline = 'alphabetic';
        oc.fillStyle    = '#fff';
        const p = outlinePad;
        for (const [dx, dy] of [[-p,-p],[0,-p],[p,-p],[-p,0],[p,0],[-p,p],[0,p],[p,p]]) {
          this.#fillTextWithSpacing(oc, text, x + dx, y + dy, spacing);
        }
        oc.globalCompositeOperation = 'source-in';
        oc.fillStyle = `rgba(0,0,0,${outlineAlpha})`;
        oc.fillRect(0, 0, W, H);
        ctx.drawImage(olCanvas, 0, 0, W, H);
      }
    }

    ctx.save();
    ctx.shadowColor   = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur    = 3;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    ctx.drawImage(texCanvas, 0, 0, W, H);
    ctx.restore();
  }

  // ── Frame overlay ──────────────────────────────────────────────────────

  #drawFrameOverlay(ctx, frameImg, textureImg, bounds, W, H) {
    if (!frameImg) return;

    const fw = Math.round(bounds.w);
    const fh = Math.round(bounds.h);
    const fx = Math.round(bounds.x + (bounds.w - fw) / 2);
    const fy = Math.round(bounds.y + (bounds.h - fh) / 2);

    const { canvas: maskCanvas, ctx: mc } = this.#makeOffscreen(W, H);
    mc.drawImage(frameImg, fx, fy, fw, fh);

    const { canvas: texCanvas, ctx: tc } = this.#makeOffscreen(W, H);
    const frameTone = this.#getMotifEngravingStyle();
    this.#paintBrushedMetal(tc, textureImg, fx, fy, fw, fh, {
      seed:         37.7,
      sheen:        0.2,
      streak:       0.08,
      contrast:     0.96,
      tintColor:    frameTone.tintColor,
      tintAlpha:    frameTone.tintAlpha,
      darkenAlpha:  frameTone.darkenAlpha,
      brightenAlpha: frameTone.brightenAlpha,
    });

    tc.globalCompositeOperation = 'destination-in';
    tc.drawImage(maskCanvas, 0, 0, W, H);
    tc.globalCompositeOperation = 'source-over';

    const { alpha: outlineAlpha, pad: outlinePad } = this.#getMotifOutlineAlpha();
    if (outlineAlpha > 0) {
      const { canvas: olCanvas, ctx: oc } = this.#makeOffscreen(W, H);
      if (oc) {
        oc.drawImage(frameImg, fx - outlinePad, fy - outlinePad,
                               fw + outlinePad * 2, fh + outlinePad * 2);
        oc.globalCompositeOperation = 'source-in';
        oc.fillStyle = `rgba(0,0,0,${outlineAlpha})`;
        oc.fillRect(0, 0, W, H);
        ctx.drawImage(olCanvas, 0, 0, W, H);
      }
    }
    ctx.drawImage(texCanvas, 0, 0, W, H);
  }

  // ── Brushed-metal painting ─────────────────────────────────────────────

  #paintBrushedMetal(ctx, textureImg, x, y, w, h, options = {}) {
    const seed          = options.seed          || 0;
    const sheen         = options.sheen         || 0.2;
    const streak        = options.streak        || 0.1;
    const contrast      = options.contrast      || 1;
    const tintColor     = options.tintColor     || 'rgb(208,208,208)';
    const tintAlpha     = options.tintAlpha     ?? 0.14;
    const darkenAlpha   = options.darkenAlpha   ?? 0.1;
    const brightenAlpha = options.brightenAlpha ?? 0.06;
    const baseTop       = options.baseTop       || '#d5d5d5';
    const baseMid       = options.baseMid       || '#eeeeee';
    const baseBottom    = options.baseBottom    || '#cbcbcb';

    const grad = ctx.createLinearGradient(x, y, x, y + h);
    grad.addColorStop(0,   baseTop);
    grad.addColorStop(0.5, baseMid);
    grad.addColorStop(1,   baseBottom);
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);

    // Deterministic horizontal streaks
    for (let iy = 0; iy < h; iy += 2) {
      const r = this.#rand(seed + iy * 13.17);
      const a = (0.02 + r * streak) * contrast;
      ctx.fillStyle = `rgba(255,255,255,${a.toFixed(4)})`;
      ctx.fillRect(x, y + iy, w, 1);
    }

    const shadow = ctx.createLinearGradient(x, y, x, y + h);
    shadow.addColorStop(0,    'rgba(0,0,0,0.14)');
    shadow.addColorStop(0.12, 'rgba(0,0,0,0)');
    shadow.addColorStop(0.88, 'rgba(0,0,0,0)');
    shadow.addColorStop(1,    'rgba(0,0,0,0.16)');
    ctx.fillStyle = shadow;
    ctx.fillRect(x, y, w, h);

    ctx.fillStyle = this.#toRgba(tintColor, tintAlpha);
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = `rgba(0,0,0,${darkenAlpha})`;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = `rgba(255,255,255,${brightenAlpha})`;
    ctx.fillRect(x, y, w, h);

    const sheenGrad = ctx.createLinearGradient(x, y, x + w, y + h * 0.2);
    sheenGrad.addColorStop(0,    'rgba(255,255,255,0)');
    sheenGrad.addColorStop(0.42, `rgba(255,255,255,${(sheen * 0.6).toFixed(3)})`);
    sheenGrad.addColorStop(0.5,  `rgba(255,255,255,${sheen.toFixed(3)})`);
    sheenGrad.addColorStop(0.58, `rgba(255,255,255,${(sheen * 0.6).toFixed(3)})`);
    sheenGrad.addColorStop(1,    'rgba(255,255,255,0)');
    ctx.fillStyle = sheenGrad;
    ctx.fillRect(x, y, w, h);

    if (textureImg) {
      ctx.save();
      ctx.globalAlpha = 0.18;
      ctx.drawImage(textureImg, x, y, w, h);
      ctx.restore();
    }
  }

  // ── Layout helpers ─────────────────────────────────────────────────────

  #getFrameBounds(W, H) {
    const b = YuniqCardPreview.#FRAME_BOUNDS;
    return {
      x: Math.round(W * b.x),
      y: Math.round(H * b.y),
      w: Math.round(W * b.w),
      h: Math.round(H * b.h),
    };
  }

  #getChipSafeRect(W, H) {
    const chipW = W * 0.17, chipH = H * 0.18;
    const chipX = W * 0.09, chipY = H * 0.235;
    const padX  = W * 0.015, padY = H * 0.012;
    return { x: chipX - padX, y: chipY - padY, w: chipW + padX * 2, h: chipH + padY * 2 };
  }

  #resolveMotifPlacement({ motifImg, W, H, targetHeightRatio, maxWidth, centerXRatio, centerYRatio, chipRect, scaleX = null }) {
    const minScale     = 0.12;
    const targetMotifH = H * targetHeightRatio;
    const heightScale  = targetMotifH / Math.max(1, motifImg.height);
    const widthScale   = maxWidth / Math.max(1, motifImg.width);
    let motifScale     = Math.max(minScale, Math.min(heightScale, widthScale));
    const placement    = { x: 0, y: 0, w: W, h: H };
    const bottomLimit  = placement.y + placement.h * 0.95;

    let motifW = motifImg.width  * motifScale;
    let motifH = motifImg.height * motifScale;
    let motifX = W * centerXRatio - motifW / 2;
    let motifY = H * centerYRatio - motifH / 2;
    ({ x: motifX, y: motifY } = this.#clampRectInside(motifX, motifY, motifW, motifH, placement));

    for (let i = 0; i < 4; i++) {
      if (!chipRect || !this.#rectsIntersect({ x: motifX, y: motifY, w: motifW, h: motifH }, chipRect)) break;
      motifX = Math.max(motifX, chipRect.x + chipRect.w + W * 0.02);
      ({ x: motifX, y: motifY } = this.#clampRectInside(motifX, motifY, motifW, motifH, placement));
      if (!this.#rectsIntersect({ x: motifX, y: motifY, w: motifW, h: motifH }, chipRect)) break;
      motifY = Math.max(motifY, chipRect.y + chipRect.h + H * 0.012);
      ({ x: motifX, y: motifY } = this.#clampRectInside(motifX, motifY, motifW, motifH, placement));
      if (motifY + motifH <= bottomLimit) break;
      const allowedH = Math.max(H * 0.12, bottomLimit - motifY);
      motifScale = Math.max(minScale, Math.min(motifScale * 0.9, allowedH / Math.max(1, motifImg.height)));
      motifW = motifImg.width  * motifScale;
      motifH = motifImg.height * motifScale;
      motifX = W * centerXRatio - motifW / 2;
      ({ x: motifX, y: motifY } = this.#clampRectInside(motifX, motifY, motifW, motifH, placement));
      break;
    }

    if (scaleX != null) {
      const cx = motifX + motifW / 2;
      motifW = motifW * scaleX;
      motifX = cx - motifW / 2;
    }

    return { motifW, motifH, motifX, motifY };
  }

  #clampRectInside(x, y, w, h, bounds) {
    return {
      x: Math.min(Math.max(x, bounds.x), Math.max(bounds.x, bounds.x + bounds.w - w)),
      y: Math.min(Math.max(y, bounds.y), Math.max(bounds.y, bounds.y + bounds.h - h)),
    };
  }

  #rectsIntersect(a, b) {
    return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
  }

  // ── Finish-aware style helpers ─────────────────────────────────────────

  #getMotifEngravingStyle() {
    const key = this.#state.blankKey || '';
    if (/silver/.test(key))
      return { tintColor: 'rgb(0,0,0)', tintAlpha: 0.80, darkenAlpha: 0.40, brightenAlpha: 0 };
    if (/black_(matt_gold|gold_matt)/.test(key))
      return { tintColor: 'rgb(215,168,40)', tintAlpha: 0.82, darkenAlpha: 0.02, brightenAlpha: 0.24 };
    if (/gold|rose|pink/.test(key))
      return { tintColor: 'rgb(210,210,210)', tintAlpha: 0.20, darkenAlpha: 0.24, brightenAlpha: 0.16 };
    return { tintColor: 'rgb(175,182,195)', tintAlpha: 0.52, darkenAlpha: 0.08, brightenAlpha: 0.10 };
  }

  #getMotifOutlineAlpha() {
    const key = this.#state.blankKey || '';
    if (/black_(matt_gold|gold_matt)/.test(key)) return { alpha: 0.22, pad: 1 };
    if (/gold|rose/.test(key))    return { alpha: 0.46, pad: 1 };
    return { alpha: 0, pad: 1 };
  }

  // ── Image helpers ──────────────────────────────────────────────────────

  #loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (this.#imgCache.has(url)) return Promise.resolve(this.#imgCache.get(url));
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload  = () => { this.#imgCache.set(url, img); resolve(img); };
      img.onerror = () => {
        // CORS-gated load failed (cached non-CORS response) — retry without crossOrigin
        const img2 = new Image();
        img2.onload  = () => { this.#imgCache.set(url, img2); resolve(img2); };
        img2.onerror = () => resolve(null);
        img2.src = url;
      };
      img.src     = url;
    });
  }

  #toLumaMask(img) {
    if (!img) return img;
    if (this.#motifMaskCache.has(img)) return this.#motifMaskCache.get(img);
    const w = img.naturalWidth  || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return img;
    const c   = document.createElement('canvas');
    c.width   = w;
    c.height  = h;
    const ctx = c.getContext('2d');
    if (!ctx) return img;
    try {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0);
      const data = ctx.getImageData(0, 0, w, h);
      const d    = data.data;
      for (let i = 0; i < d.length; i += 4) {
        const a = d[i + 3] / 255;
        if (a <= 0) continue;
        const lum = d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
        const s   = 1 - lum / 255;
        d[i + 3]  = Math.round(Math.max(0, Math.min(1, Math.pow(s, 0.6) * a)) * 255);
      }
      ctx.putImageData(data, 0, 0);
      this.#motifMaskCache.set(img, c);
      return c;
    } catch (_) { return img; }
  }

  #getStrippedBlank(img) {
    if (!img) return null;
    const url = img.src || '';
    if (this.#processedBlankCache.has(url)) return this.#processedBlankCache.get(url);
    const w   = img.naturalWidth  || img.width  || 856;
    const h   = img.naturalHeight || img.height || 540;
    const sc  = this.#bufScale;
    const c   = document.createElement('canvas');
    c.width   = Math.round(w * sc);
    c.height  = Math.round(h * sc);
    const ctx = c.getContext('2d');
    if (!ctx) { this.#processedBlankCache.set(url, img); return img; }
    ctx.setTransform(sc, 0, 0, sc, 0, 0);
    ctx.drawImage(img, 0, 0, w, h);
    try {
      if (ctx.getImageData(0, 0, 1, 1).data[3] < 10) {
        this.#processedBlankCache.set(url, img);
        return img;
      }
      this.#stripBackground(c, ctx);
      this.#processedBlankCache.set(url, c);
      return c;
    } catch (_) {
      // Canvas tainted (CORS cache) — use original image directly
      this.#processedBlankCache.set(url, img);
      return img;
    }
  }

  #stripBackground(canvas, ctx) {
    const w  = canvas.width, h = canvas.height;
    const id = ctx.getImageData(0, 0, w, h);
    const px = id.data;
    let bgR  = 0, bgG = 0, bgB = 0;
    for (const i of [0, w - 1, (h - 1) * w, (h - 1) * w + (w - 1)]) {
      bgR += px[i * 4]; bgG += px[i * 4 + 1]; bgB += px[i * 4 + 2];
    }
    bgR = Math.round(bgR / 4); bgG = Math.round(bgG / 4); bgB = Math.round(bgB / 4);
    if (0.299 * bgR + 0.587 * bgG + 0.114 * bgB < 150) return;
    const tol     = 40;
    const isBg    = (i) => {
      const p = i * 4;
      return px[p + 3] > 200 &&
        Math.abs(px[p] - bgR) + Math.abs(px[p + 1] - bgG) + Math.abs(px[p + 2] - bgB) < tol * 3;
    };
    const visited = new Uint8Array(w * h);
    const queue   = new Int32Array(w * h);
    let head = 0, tail = 0;
    const enqueue = (i) => { if (!visited[i] && isBg(i)) { visited[i] = 1; queue[tail++] = i; } };
    for (let x = 0; x < w; x++)         { enqueue(x); enqueue((h - 1) * w + x); }
    for (let y = 1; y < h - 1; y++)     { enqueue(y * w); enqueue(y * w + w - 1); }
    while (head < tail) {
      const idx = queue[head++];
      const x   = idx % w, y = (idx / w) | 0;
      if (x > 0)     enqueue(idx - 1);
      if (x < w - 1) enqueue(idx + 1);
      if (y > 0)     enqueue(idx - w);
      if (y < h - 1) enqueue(idx + w);
    }
    for (let i = 0; i < w * h; i++) { if (visited[i]) px[i * 4 + 3] = 0; }
    ctx.putImageData(id, 0, 0);
  }

  // ── Misc ───────────────────────────────────────────────────────────────

  #rand(v) {
    const s = Math.sin(v * 12.9898) * 43758.5453;
    return s - Math.floor(s);
  }

  #toRgba(color, alpha) {
    const m = String(color).match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+)?\s*\)/i);
    if (!m) return `rgba(200,200,200,${alpha})`;
    return `rgba(${m[1]},${m[2]},${m[3]},${alpha})`;
  }

  #measureTextWidth(ctx, text, spacing) {
    let w = 0;
    for (let i = 0; i < text.length; i++) {
      w += ctx.measureText(text[i]).width;
      if (i < text.length - 1) w += spacing;
    }
    return w;
  }

  #fillTextWithSpacing(ctx, text, x, y, spacing) {
    let cursor = x;
    for (const char of text) {
      ctx.fillText(char, cursor, y);
      cursor += ctx.measureText(char).width + spacing;
    }
  }

  #roundRect(ctx, x, y, w, h, r) {
    const radius = Math.max(0, Math.min(r, Math.floor(Math.min(w, h) / 2)));
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + w - radius, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
    ctx.lineTo(x + w, y + h - radius);
    ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
    ctx.lineTo(x + radius, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  #getBackCardRect(blankImg, W, H) {
    if (!blankImg) return { x: 0, y: 0, w: W, h: H };
    try {
      const off = document.createElement('canvas');
      off.width = W; off.height = H;
      const oc = off.getContext('2d');
      if (!oc) return { x: 0, y: 0, w: W, h: H };
      oc.drawImage(blankImg, 0, 0, W, H);
      const data = oc.getImageData(0, 0, W, H).data;
      let minX = W, minY = H, maxX = -1, maxY = -1;
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          if (data[((y * W) + x) * 4 + 3] <= 8) continue;
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
      if (maxX < minX || maxY < minY) return { x: 0, y: 0, w: W, h: H };
      const rect = { x: Math.max(0, minX), y: Math.max(0, minY), w: maxX - minX + 1, h: maxY - minY + 1 };
      if (rect.w < W * 0.5 || rect.h < H * 0.5) return { x: 0, y: 0, w: W, h: H };
      return rect;
    } catch (e) {
      return { x: 0, y: 0, w: W, h: H };
    }
  }
}

if (!customElements.get('yuniq-card-preview')) {
  customElements.define('yuniq-card-preview', YuniqCardPreview);
}
